import { useState, useEffect } from "react";
import { useAuth, apiClient } from "@/App";
import { toast } from "sonner";
import {
  FileText,
  Download,
  Filter,
  RefreshCw,
  User,
  Calendar,
  Activity,
  PlusCircle,
  Edit,
  Trash2,
  LogIn,
  FileDown,
  AlertTriangle,
  Shield
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function AuditLog() {
  const { isFounder } = useAuth();
  const [logs, setLogs] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState("all");
  const [filterEntity, setFilterEntity] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  useEffect(() => {
    if (isFounder) {
      fetchData();
    }
  }, [isFounder, filterAction, filterEntity]);

  const fetchData = async () => {
    try {
      let url = "/audit-logs?limit=200";
      if (filterAction !== "all") url += `&action=${filterAction}`;
      if (filterEntity !== "all") url += `&entity_type=${filterEntity}`;
      if (startDate && endDate) url += `&start_date=${startDate}&end_date=${endDate}`;
      
      const [logsRes, summaryRes] = await Promise.all([
        apiClient.get(url),
        apiClient.get("/audit-logs/summary")
      ]);
      setLogs(logsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      console.error("Failed to fetch audit logs:", error);
      toast.error("Failed to load audit logs");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      let url = "/export/audit-logs?format=json";
      if (startDate && endDate) url += `&start_date=${startDate}&end_date=${endDate}`;
      
      const response = await apiClient.get(url);
      const blob = new Blob([JSON.stringify(response.data.data, null, 2)], { type: "application/json" });
      const urlObj = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = urlObj;
      a.download = response.data.filename;
      a.click();
      toast.success("Audit logs exported!");
    } catch (error) {
      toast.error("Failed to export audit logs");
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case "create": return <PlusCircle className="w-4 h-4 text-[#10B981]" />;
      case "update": return <Edit className="w-4 h-4 text-[#3B82F6]" />;
      case "delete": return <Trash2 className="w-4 h-4 text-red-400" />;
      case "login": return <LogIn className="w-4 h-4 text-[#A78BFA]" />;
      case "export": return <FileDown className="w-4 h-4 text-[#F59E0B]" />;
      case "clear_data": return <AlertTriangle className="w-4 h-4 text-red-500" />;
      default: return <Activity className="w-4 h-4 text-gray-400" />;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case "create": return "bg-[#10B981]/10 text-[#10B981]";
      case "update": return "bg-[#3B82F6]/10 text-[#3B82F6]";
      case "delete": return "bg-red-400/10 text-red-400";
      case "login": return "bg-[#A78BFA]/10 text-[#A78BFA]";
      case "export": return "bg-[#F59E0B]/10 text-[#F59E0B]";
      case "clear_data": return "bg-red-500/10 text-red-500";
      default: return "bg-gray-700 text-gray-300";
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (!isFounder) {
    return (
      <div className="page-content">
        <div className="text-center py-20">
          <Shield className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-white mb-2">Access Restricted</h2>
          <p className="text-gray-500">Only founders can access the audit log.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="page-content">
        <div className="space-y-6">
          <div className="h-8 skeleton w-1/4"></div>
          <div className="grid grid-cols-5 gap-4">
            {[1, 2, 3, 4, 5].map((i) => (<div key={i} className="h-24 skeleton"></div>))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-content animate-fade-in" data-testid="audit-log-page">
      {/* Header */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Audit Log</h1>
          <p className="page-subtitle">Track all system activities and changes</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport} className="border-[#262626]" data-testid="export-audit-btn">
            <Download className="w-4 h-4 mr-2" /> Export
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-6 gap-4 mb-6">
          <div className="bg-[#1A1A1A] border border-[#262626] rounded p-4 animate-fade-in-up stagger-1">
            <div className="flex items-center gap-2 mb-2">
              <Activity className="w-4 h-4 text-[#FF4500]" />
              <span className="text-xs text-gray-500 uppercase">Total Logs</span>
            </div>
            <p className="text-2xl font-bold text-white">{summary.total_logs || 0}</p>
          </div>
          <div className="bg-[#1A1A1A] border border-[#262626] rounded p-4 animate-fade-in-up stagger-2">
            <div className="flex items-center gap-2 mb-2">
              <Calendar className="w-4 h-4 text-[#3B82F6]" />
              <span className="text-xs text-gray-500 uppercase">Today</span>
            </div>
            <p className="text-2xl font-bold text-white">{summary.today_count || 0}</p>
          </div>
          <div className="bg-[#1A1A1A] border border-[#262626] rounded p-4 animate-fade-in-up stagger-3">
            <div className="flex items-center gap-2 mb-2">
              <PlusCircle className="w-4 h-4 text-[#10B981]" />
              <span className="text-xs text-gray-500 uppercase">Creates</span>
            </div>
            <p className="text-2xl font-bold text-[#10B981]">{summary.action_counts?.create || 0}</p>
          </div>
          <div className="bg-[#1A1A1A] border border-[#262626] rounded p-4 animate-fade-in-up stagger-4">
            <div className="flex items-center gap-2 mb-2">
              <Edit className="w-4 h-4 text-[#3B82F6]" />
              <span className="text-xs text-gray-500 uppercase">Updates</span>
            </div>
            <p className="text-2xl font-bold text-[#3B82F6]">{summary.action_counts?.update || 0}</p>
          </div>
          <div className="bg-[#1A1A1A] border border-[#262626] rounded p-4 animate-fade-in-up stagger-5">
            <div className="flex items-center gap-2 mb-2">
              <Trash2 className="w-4 h-4 text-red-400" />
              <span className="text-xs text-gray-500 uppercase">Deletes</span>
            </div>
            <p className="text-2xl font-bold text-red-400">{summary.action_counts?.delete || 0}</p>
          </div>
          <div className="bg-[#1A1A1A] border border-[#262626] rounded p-4 animate-fade-in-up stagger-6">
            <div className="flex items-center gap-2 mb-2">
              <FileDown className="w-4 h-4 text-[#F59E0B]" />
              <span className="text-xs text-gray-500 uppercase">Exports</span>
            </div>
            <p className="text-2xl font-bold text-[#F59E0B]">{summary.action_counts?.export || 0}</p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <Select value={filterAction} onValueChange={setFilterAction}>
          <SelectTrigger className="w-40 bg-[#1A1A1A] border-[#262626]">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Action" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-[#262626]">
            <SelectItem value="all">All Actions</SelectItem>
            <SelectItem value="create">Create</SelectItem>
            <SelectItem value="update">Update</SelectItem>
            <SelectItem value="delete">Delete</SelectItem>
            <SelectItem value="login">Login</SelectItem>
            <SelectItem value="export">Export</SelectItem>
            <SelectItem value="clear_data">Clear Data</SelectItem>
          </SelectContent>
        </Select>

        <Select value={filterEntity} onValueChange={setFilterEntity}>
          <SelectTrigger className="w-40 bg-[#1A1A1A] border-[#262626]">
            <FileText className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Entity" />
          </SelectTrigger>
          <SelectContent className="bg-[#1A1A1A] border-[#262626]">
            <SelectItem value="all">All Entities</SelectItem>
            <SelectItem value="client">Clients</SelectItem>
            <SelectItem value="project">Projects</SelectItem>
            <SelectItem value="payment">Payments</SelectItem>
            <SelectItem value="task">Tasks</SelectItem>
            <SelectItem value="meeting">Meetings</SelectItem>
            <SelectItem value="income">Income</SelectItem>
            <SelectItem value="expense">Expenses</SelectItem>
            <SelectItem value="system">System</SelectItem>
          </SelectContent>
        </Select>

        <div className="flex items-center gap-2">
          <Input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            placeholder="Start Date"
            className="w-36 bg-[#1A1A1A] border-[#262626]"
          />
          <span className="text-gray-500">to</span>
          <Input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            placeholder="End Date"
            className="w-36 bg-[#1A1A1A] border-[#262626]"
          />
          <Button variant="outline" onClick={fetchData} className="border-[#262626]">
            Apply
          </Button>
        </div>

        <Button variant="ghost" onClick={() => { setFilterAction("all"); setFilterEntity("all"); setStartDate(""); setEndDate(""); fetchData(); }} className="text-gray-400">
          <RefreshCw className="w-4 h-4 mr-2" /> Reset
        </Button>
      </div>

      {/* Logs Table */}
      <div className="bg-[#1A1A1A] border border-[#262626] rounded overflow-hidden">
        {logs.length > 0 ? (
          <table className="data-table w-full">
            <thead>
              <tr>
                <th className="w-10"></th>
                <th>Action</th>
                <th>Entity</th>
                <th>Details</th>
                <th>User</th>
                <th>Role</th>
                <th>Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.log_id}>
                  <td>{getActionIcon(log.action)}</td>
                  <td>
                    <span className={`px-2 py-1 rounded text-xs capitalize ${getActionColor(log.action)}`}>
                      {log.action?.replace("_", " ")}
                    </span>
                  </td>
                  <td>
                    <span className="text-white capitalize">{log.entity_type}</span>
                    {log.entity_name && (
                      <span className="text-gray-500 text-sm ml-2">({log.entity_name})</span>
                    )}
                  </td>
                  <td className="text-gray-400 text-sm">
                    {log.details ? (
                      <span className="truncate max-w-[200px] block">
                        {typeof log.details === "object" 
                          ? Object.entries(log.details).slice(0, 2).map(([k, v]) => `${k}: ${typeof v === "object" ? "..." : v}`).join(", ")
                          : log.details}
                      </span>
                    ) : "-"}
                  </td>
                  <td>
                    <div className="flex items-center gap-2">
                      <User className="w-3 h-3 text-gray-500" />
                      <span className="text-white">{log.user_name}</span>
                    </div>
                  </td>
                  <td>
                    <span className="badge bg-gray-700 text-gray-300 capitalize">
                      {log.user_role?.replace("_", " ")}
                    </span>
                  </td>
                  <td className="text-gray-400 text-sm">{formatDate(log.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-12">
            <Activity className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-500">No audit logs found</p>
          </div>
        )}
      </div>
    </div>
  );
}
